import { useEffect, useMemo, useState } from 'react'
import { bakeSettingsStore } from '../lib/bakeSettingsStore'
import { startBake } from '../lib/bakeStatusStore'
import { getSplatBounds, lightProbeCount, reflectionCount } from '../lib/bakeLayout'
import DraggablePanel from './DraggablePanel'

/**
 * BakeSettingsPanel — floating settings window shown when the user clicks "Bake
 * Lighting" (ENG-561). Numeric inputs for light-probe + reflection density, with
 * a live count for each (computed from the loaded splat's bounds). The matching
 * placement gizmos render in the viewport (BakePreviewGizmos). Confirm fires the
 * bake with these params; Cancel closes without baking.
 */

const lbl = { display: 'block', color: '#9ca3af', fontSize: 10, margin: '8px 0 2px' }
const inp = {
  width: 70, padding: '3px 6px', borderRadius: 4, border: '1px solid #3a3a3a',
  background: '#111', color: '#e5e7eb', font: '500 12px var(--m-font)',
}

// Small show/hide toggle for the in-scene preview gizmos (view-only; doesn't
// affect the bake). Color-matched to the marker it controls.
function VizToggle({ color, checked, onChange }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
      fontSize: 10, color: checked ? '#cbd5e1' : '#6b7280', userSelect: 'none',
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ accentColor: color, cursor: 'pointer', margin: 0 }} />
      show
    </label>
  )
}

function Num({ label, value, onChange, step = 0.5, min = 0.1 }) {
  return (
    <label style={{ marginRight: 12 }}>
      <span style={lbl}>{label}</span>
      <input type="number" value={value} step={step} min={min} style={inp}
        onChange={e => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) onChange(v) }} />
    </label>
  )
}

export default function BakeSettingsPanel() {
  const [, force] = useState(0)
  useEffect(() => bakeSettingsStore.subscribe(() => force(n => n + 1)), [])
  const { open, assetId, params, show } = bakeSettingsStore.getState()
  // Compute bounds once per panel-open: getBoundingBox walks ~2M gaussians, so
  // re-running it on every render (incl. each param keystroke) froze the UI.
  // (useMemo must run before the early return — rules of hooks.)
  const bounds = useMemo(() => (open ? getSplatBounds() : null), [open])
  if (!open) return null

  const lpCount = lightProbeCount(bounds, params.lpSpacing, params.lpLayers)
  const reflCount = reflectionCount(bounds, params.reflSpacing, params.reflInset)

  return (
    <DraggablePanel title="Bake Lighting" storageKey="lighting" width={320}
      onClose={() => bakeSettingsStore.close()}>
      {!bounds && (
        <div style={{ color: '#f59e0b', fontSize: 11, margin: '6px 0' }}>
          No environment loaded — counts unavailable.
        </div>
      )}

      <div style={{ borderTop: '1px solid #262626', marginTop: 6, paddingTop: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, color: '#67e8f9', fontWeight: 600 }}>
            Light probes (diffuse) · <span style={{ color: '#e5e7eb' }}>~{lpCount} probes</span>
          </div>
          <VizToggle
            color="#22d3ee" checked={show.lightProbes}
            onChange={v => bakeSettingsStore.setShow({ lightProbes: v })}
          />
        </div>
        <Num label="spacing (m)" value={params.lpSpacing} step={0.25}
          onChange={v => bakeSettingsStore.setParams({ lpSpacing: v })} />
        <Num label="layers" value={params.lpLayers} step={1} min={1}
          onChange={v => bakeSettingsStore.setParams({ lpLayers: Math.max(1, Math.round(v)) })} />
      </div>

      <div style={{ borderTop: '1px solid #262626', marginTop: 8, paddingTop: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, color: '#fb923c', fontWeight: 600 }}>
            Reflections (specular) · <span style={{ color: '#e5e7eb' }}>~{reflCount} captures</span>
          </div>
          <VizToggle
            color="#fb923c" checked={show.reflections}
            onChange={v => bakeSettingsStore.setShow({ reflections: v })}
          />
        </div>
        <Num label="spacing (m)" value={params.reflSpacing} step={0.5}
          onChange={v => bakeSettingsStore.setParams({ reflSpacing: v })} />
        <Num label="inset (m)" value={params.reflInset} step={0.5} min={0}
          onChange={v => bakeSettingsStore.setParams({ reflInset: v })} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button onClick={() => bakeSettingsStore.close()} style={{
          padding: '5px 12px', borderRadius: 5, border: '1px solid #3a3a3a',
          background: 'transparent', color: '#cbd5e1', cursor: 'pointer', font: '600 12px var(--m-font)',
        }}>Cancel</button>
        <button onClick={() => { startBake(assetId, params); bakeSettingsStore.close() }} style={{
          padding: '5px 14px', borderRadius: 5, border: '1px solid #1f6feb',
          background: '#1f6feb', color: '#fff', cursor: 'pointer', font: '700 12px var(--m-font)',
        }}>Bake</button>
      </div>
    </DraggablePanel>
  )
}
